// we are tracking data with TefDataLayer
// see https://developers.google.com/tag-manager/quickstart

import {
  AddressType,
  AvCheckRequestBody,
  EcommerceProductValue,
  FixedLineAccessTechnologyType,
  HouseholdConfigurationType,
  TariffOfferType, TariffPositionsType,
  UsageType
} from '@/types/av-check';

declare global {
  interface Window {
    tefDataLayer: any;
  }
}

export function pushToTefDataLayer(w: Window, data: {}) {
  if (typeof w !== 'undefined' && w.tefDataLayer) {
    new TrackingServiceAdapter(w.tefDataLayer).push(data, w);
  }
}

export class TrackingServiceAdapter {
  private readonly tefDataLayer: TefDataLayer;

  constructor(tefDataLayer: TefDataLayer) {
    this.tefDataLayer = tefDataLayer;
  }

  push(data: {}, w: Window): void {
      if (w.tefDataLayer) {
        this.tefDataLayer.push(data);
      }
    // @ts-ignore
    if(w.tefDataLayer == undefined && data["eAct"].includes("CMS")){
        window.addEventListener('DOMContentLoaded', function () {
          w.tefDataLayer.push(data);
      });
    }
  }
}

const getUsageTypeAbbreviation = (usageType:UsageType) =>  (usageType===UsageType.STREAMING ? 'streaming' : (usageType===UsageType.HOME_OFFICE ? 'HO' : 'OGA'));

export function buildSuccessData(address: AddressType,
                                 householdConfiguration: HouseholdConfigurationType,
                                 ecommerceProductValues: EcommerceProductValue[],
                                 sliderTariffPositions: TariffPositionsType, isCmsCall: boolean): {} {
  const map = ecommerceProductValues
    ? ecommerceProductValues.map((ecommerceProductValue) => ({
      name: ecommerceProductValue?.name,
      list: 'vCheck Results',
      ...ecommerceProductValue?.attributes,
      position: sliderTariffPositions[ecommerceProductValue?.name] || 0,
    })) : [];

  const {usageTypes} = householdConfiguration;
  const usageTypeTrackingInfo = (usageTypes.length ? (usageTypes.map(getUsageTypeAbbreviation).join('-')) : 'undefined');
  const personInHouseTracingInfo = householdConfiguration.personInHousehold ? householdConfiguration.personInHousehold : 'no-selection';
  const elabInfo = isCmsCall ? `${address.zipCode} ${address.city}` : `${address.zipCode} ${address.city}|${personInHouseTracingInfo}|${usageTypeTrackingInfo}`;
  const eAct = isCmsCall ?  `click - verify address - CMS` : `click – verify address`;
  return {
    event: 'vCheckVerifyClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: eAct,
    eLab: elabInfo,
    eResult: 'success',
    eVal: 0,
    nonInteraction: false,
    ecommerce: {
      impressions: [
          ...map
        ]
    }
  };
}

export function buildSuccessDataPopup(offerName: string, address: string): {} {
  return {
    event: 'vCheckVerifyClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click - verify address (V-Check Pop-Up)',
    eLab: address,
    eResult: `success - DSL product is available | ${offerName}`,
    eVal: 0,
    nonInteraction: false,
  };
}

export function buildWarningData(warning: string, address: string): {} {
  return {
    event: 'vCheckVerifyClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click - verify address',
    eLab: address,
    eResult: `warning: ${warning}`,
    eVal: 0,
    nonInteraction: false,
  };
}

export function buildWarningDataPopup(warning: string, offerName: string, address: string): {} {
  return {
    event: 'vCheckVerifyClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click - verify address (V-Check Pop-Up)',
    eLab: address,
    eResult: `warning: ${warning} | ${offerName}`,
    eVal: 0,
    nonInteraction: false,
  };
}

export function buildErrorData(error: string, avCheckRequestBody: AvCheckRequestBody): {} {

  const {usageTypes} = avCheckRequestBody;
  const usageTypeTrackingInfo = (usageTypes.length ? (usageTypes.map(getUsageTypeAbbreviation).join('-')) : 'undefined');
  const personInHouseTracingInfo = avCheckRequestBody.personInHousehold ? avCheckRequestBody.personInHousehold : 'no-selection';
  const elabInfo = `${avCheckRequestBody.address.zipCode} ${avCheckRequestBody.address.city}|${personInHouseTracingInfo}|${usageTypeTrackingInfo}`;

  return {
    event: 'vCheckVerifyClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click - verify address',
    eLab: elabInfo,
    eResult: error,
    eVal: 0,
    nonInteraction: false,
  };
}

export function buildErrorDataPopup(error: string, address: string): {} {
  return {
    event: 'vCheckVerifyClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click - verify address (V-Check Pop-Up)',
    eLab: address,
    eResult: `failure: ${error}`,
    eVal: 0,
    nonInteraction: false,
  };
}

export function buildCheckOtherAddressData(address: string): {} {
  return {
    event: 'vCheckNewVerifyClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click - verify new address',
    eLab: address,
    eVal: 0,
    nonInteraction: false,
  };
}

export function buildCheckOtherAddressDataPopup(address: string, offerName: string): {} {
  return {
    event: 'vCheckNewVerifyClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click - verify new address (V-Check Pop-Up)',
    eLab: `${address} | ${offerName}`,
    eVal: 0,
    nonInteraction: false,
  };
}

export function buildContinueWithSelectedTariffDataPopup(selectedOfferName: string, originalOfferName: string, useCase: string): {} {
  return {
    event: 'vCheckContinueClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click - '+`${useCase}`+' (V-Check Pop-Up)',
    fromProduct: `${originalOfferName}`,
    toProduct: `${selectedOfferName}`,
    eVal: 0,
    nonInteraction: false,
  };
}

export function buildDataForProductClick(selectedTariff: TariffOfferType,
                                         highlighted: boolean,
                                         ecommerceProductValues: EcommerceProductValue[],
                                         sliderTariffPositions: TariffPositionsType) {
  const selectedTariffName = selectedTariff.offerName;
  const ecommerceProductVal = ecommerceProductValues.find((ecommerceProductValue) => selectedTariffName === ecommerceProductValue.name);
  const selectedTariffPosition = sliderTariffPositions[selectedTariffName] || 0;

  return {
    event: 'vCheckProductClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click - tariff',
    eLab: selectedTariffName,
    eAccessTechnologieOptions: selectedTariff.accessTechnology,
    eIsHighlighted: highlighted,
    eVal: selectedTariffPosition,
    nonInteraction: false,
    ecommerce: {
      click: {
        actionField: {
          list: 'vCheck Results',
        },
        products: [
          {
            name: ecommerceProductVal?.name,
            ...ecommerceProductVal?.attributes,
            position: selectedTariffPosition,
          }
        ]
      }
    },
    eventCallback() {
      document.location.href = selectedTariff.url;
    }
  };
}

export function buildDataForChooseTechnologyClick(
    selectedTariff: TariffOfferType,
    accessTechs: string,
    highlighted: boolean,
    sliderTariffPositions: TariffPositionsType
) {

  return {
    event: 'vCheckChooseTechnologyClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click –choose access technology',
    eLab: selectedTariff.offerName,
    eAccessTechnologieOptions: accessTechs,
    eIsHighlighted: highlighted,
    eVal: sliderTariffPositions[selectedTariff.offerName] || 0,
    nonInteraction: false
  };
}

export function buildDataForChooseFtthClick(
    selectedTariff: TariffOfferType,
    accessTechnology: string,
    highlighted: boolean,
    sliderTariffPositions: TariffPositionsType
) {
  return {
    event: 'vCheckChooseFtthTechnologyClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click –choose access technology',
    eLab: selectedTariff.offerName,
    eAccessTechnologieOptions: accessTechnology,
    eIsHighlighted: highlighted,
    eVal: sliderTariffPositions[selectedTariff.offerName] || 0,
    nonInteraction: false
  };
}

export function buildDataForQuestionareModuleDisplay(
    selectedTariff: TariffOfferType,
    sliderTariffPositions: TariffPositionsType
) {
  return {
    event: 'vCheckQuestionareModuleDisplay',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'display – questionare module',
    eLab: selectedTariff.offerName,
    eVal: sliderTariffPositions[selectedTariff.offerName] || 0,
    nonInteraction: true
  };
}

export function buildDataForQuestionareModuleClose(
    selectedTariff: TariffOfferType,
    sliderTariffPositions: TariffPositionsType
) {
  return {
    event: 'vCheckQuestionareModuleCloseClick',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click – questionare module close',
    eLab: selectedTariff.offerName,
    eVal: sliderTariffPositions[selectedTariff.offerName] || 0,
    nonInteraction: false
  };
}

export function buildDataForQuestionareModuleContinue(
    selectedTariff: TariffOfferType,
    sliderTariffPositions: TariffPositionsType,
    buildingPartName: string,
    fiberglassAvailable:string,
    resultType: string

) {
  const elabInfo = `product-name:${ selectedTariff.offerName}|apartment-location: ${buildingPartName}|fiberglass-available:${fiberglassAvailable}`;
  const warnMessage = `${ resultType}|${selectedTariff.offerName}`;
  return {
    event: 'vCheckQuestionareModuleClickContinue',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click – questionare module continue',
    eLab: elabInfo,
    eVal: sliderTariffPositions[selectedTariff.offerName] || 0,
    eResult: resultType.includes('warning') ? warnMessage : resultType,
    nonInteraction: false
  };
}

export function buildDataForProductClickSelectTechnology(
  selectedTariff: TariffOfferType,
  highlighted: boolean,
  ecommerceProductValues: EcommerceProductValue[],
  modalTariffPositions: TariffPositionsType
) {
  const selectedTariffName = selectedTariff.offerName;
  const ecommerceProductVal = ecommerceProductValues.find((ecommerceProductValue) => selectedTariffName === ecommerceProductValue.name);
  const selectedTariffPosition = modalTariffPositions[selectedTariffName] || 0;

  return {
    event: 'vCheckProductClickSelectTechnology',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'click –tariff -select technology',
    eLab: selectedTariffName,
    eAccessTechnologieOptions: selectedTariff.accessTechnology,
    eIsHighlighted: highlighted,
    eVal: selectedTariffPosition,
    nonInteraction: false,
    ecommerce: {
      click: {
        actionField: {
          list: 'vCheck Results',
        },
        products: [
          {
            name: ecommerceProductVal?.name,
            ...ecommerceProductVal?.attributes,
            position: selectedTariffPosition,
          }
        ]
      }
    },
    eventCallback() {
      if (selectedTariff.accessType != 'DTAG-FTTH-ETH') {
        document.location.href = selectedTariff.url;
      }
    }
  };
}

export function buildSuccessDataUpdate(ecommerceProductValues: EcommerceProductValue[], sliderTariffPosition: TariffPositionsType): {} {
  const map = ecommerceProductValues.map((ecommerceProductValue) => ({
    name: ecommerceProductValue.name,
    list: 'vCheck Results',
    ...ecommerceProductValue.attributes,
    position: sliderTariffPosition[ecommerceProductValue.name] || 0,
  }));

  return {
    event: 'vCheckProductUpdate',
    eCat: 'DSL - Verfügbarkeitscheck',
    eAct: 'productUpdate',
    eVal: 0,
    nonInteraction: true,
    ecommerce: {
      impressions: [
        ...map
      ]
    }
  };
}

export function buildCheckPersonAndUsecaseData(numberOfPersons: string, useCaseCheck: string): {} {
  return {
    event: 'vCheckPersonAndUseCase',
    personCheck: numberOfPersons,
    useCaseCheck: useCaseCheck,
  };
}

export function buildDataForInitialization(): {} {
  return {
    event: 'init',
    page:{
      content: 'internet-festnetz',
      category: 'availability-check',
      productCategory : '',
      application: 'e-shop',
      template: 'whitelabel-availability-check-dev'
    }
  };
}

export interface TefDataLayer {
  push(data: {}): void;
}
